import {
  Table,
  TableBodyInfiniteScroll,
  TableHeader
} from '@library/components/table';
import { useCallback, useMemo } from 'react';
import { EMPTY_MEMBERS_RESPONSE } from '@bo-constants/member/members';
import type { FormattedMembersResponse } from '@library/types/members';
import { MembersRow } from '@library/components/table/rows/MembersRow';
import { NoResults } from '@library/components/NoResults';
import { getMembersTableHeader } from '@bo-utils/members/members';
import { useNavigate } from 'react-router-dom';

export interface MembersTableProps {
  formattedMembers: FormattedMembersResponse | undefined;
  onPageChange: () => void;
}

export const MembersTable = ({
  formattedMembers,
  onPageChange
}: MembersTableProps) => {
  const { currentPage, members, totalPages } =
    formattedMembers ?? EMPTY_MEMBERS_RESPONSE;
  const navigate = useNavigate();

  const handleRowClick = useCallback(
    (id: string) => {
      navigate(`/members/${id}/profile`);
    },
    [navigate]
  );

  const tableHeader = useMemo(() => getMembersTableHeader(), []);

  const infiniteScrollProps = useMemo(
    () => ({
      dataLength: members?.length ?? 0,
      hasMore: totalPages > currentPage,
      loader: <p>Loading...</p>,
      next: onPageChange
    }),
    [members, currentPage, onPageChange, totalPages]
  );

  if (!members?.length) {
    return (
      <NoResults
        className="m-0"
        dataTestId="no-member-search-results"
        label="Use the search bar to search by Name, Email, or Member ID"
      />
    );
  }

  return (
    <Table
      className="border-soFresh-140 overflow-x-auto rounded-lg border p-0"
      dataTestId="members-search-results"
    >
      <TableHeader headers={tableHeader} className="px-4" />
      <TableBodyInfiniteScroll
        bodyHeight="[calc(100vh-22rem)]"
        bodyMaxHeight="max-h-[calc(100vh-22rem)]"
        infiniteScrollProps={infiniteScrollProps}
        scrollId="MemberNotesTableBody"
      >
        {members.map((member) => (
          <MembersRow
            key={member.id}
            member={member}
            onRowClick={handleRowClick}
          />
        ))}
      </TableBodyInfiniteScroll>
    </Table>
  );
};
